<script>
import * as m from "@app/paraglide/messages.js";
import {
    eventGroupState,
    isSelectedEventGroupLocked,
} from "@app/store/eventGroupStore.svelte.js";
import { lockState } from "@app/store/lockStore.svelte.js";
import { projectState } from "@app/store/projectStore.svelte.js";
import { editorState } from "@app/store/editorStore.svelte.js";
import { commitHistory } from "@app/store/historyStore.svelte.js";
import { audioScrub } from "@app/store/audioScrubStore.svelte.js";
import tcLib from "@cclib/lib/timecode.js";

/** @type {{ row: any, column: any }} */
let { row, column } = $props();

/** @type {"start" | "end"} */
let tcType = $derived(column?.id === "end" ? "end" : "start");

let event = $derived(
    eventGroupState[projectState.selected]?.events?.find(
        (e) => e.id === row?.id,
    ),
);

let canEdit = $derived(!!event && !isSelectedEventGroupLocked());
let tc = $state("--:--:--:--");
/** Avoid overwriting in-progress typing when syncing from store */
let focused = $state(false);

function formatFromEvent() {
    if (!event) {
        tc = "--:--:--:--";
        return;
    }
    const sec = event[tcType];
    if (sec === false || sec == null || Number.isNaN(sec)) {
        tc = "--:--:--:--";
        return;
    }
    try {
        tc = tcLib.secToTc(
            sec + projectState.incode,
            projectState.frameRate,
            projectState.dropFrame,
        );
    } catch {
        tc = "--:--:--:--";
    }
}

$effect(() => {
    // Depend on live event TC + project TC settings
    void event?.[tcType];
    void projectState.frameRate;
    void projectState.incode;
    void projectState.dropFrame;
    if (!focused) {
        formatFromEvent();
    }
});

/**
 * @param {MouseEvent} e
 */
function setTc(e) {
    e.preventDefault();
    e.stopPropagation();

    if (!canEdit || !event) return;
    if (typeof player === "undefined" || !player) return;

    const originalDuration =
        event.end === false || event.start === false
            ? 2
            : event.end - event.start;

    const originalId = event.id;
    const egIdx = projectState.selected;
    const eventIndex = eventGroupState[egIdx].events.findIndex(
        (ev) => ev.id === originalId,
    );
    const playerTime = player.currentTime;
    const minFrameGap = editorState.minFrameGap;

    tc = tcLib.secToTc(
        playerTime + projectState.incode,
        projectState.frameRate,
        projectState.dropFrame,
    );

    commitHistory({
        name: "update timecode",
        eventGroup: egIdx,
        mutate: {
            eventGroupState: (draft) => {
                const ev = draft[egIdx].events[eventIndex];
                if (!ev) return;
                ev[tcType] = playerTime;

                if (tcType === "start") {
                    if (isNaN(ev.end)) {
                        ev.end = playerTime + 2;
                    } else if (ev.end < ev.start) {
                        ev.end = ev.start + originalDuration;
                    }

                    if (
                        minFrameGap > 0 &&
                        eventIndex > 0 &&
                        ev.start <= draft[egIdx].events[eventIndex - 1].end
                    ) {
                        draft[egIdx].events[eventIndex - 1].end =
                            ev.start - minFrameGap / projectState.frameRate;
                    }
                } else if (tcType === "end") {
                    if (isNaN(ev.start)) {
                        ev.start = playerTime - 2;
                    } else if (ev.start > ev.end) {
                        ev.start = ev.end - originalDuration;
                    }

                    if (
                        minFrameGap > 0 &&
                        eventIndex < draft[egIdx].events.length - 1 &&
                        ev.end >= draft[egIdx].events[eventIndex + 1].start
                    ) {
                        draft[egIdx].events[eventIndex + 1].start =
                            ev.end + minFrameGap / projectState.frameRate;
                    }
                }

                const newIndex = draft[egIdx].events.findIndex(
                    (item) => item.id === originalId,
                );
                draft[egIdx].selected = [newIndex];
            },
        },
    });
}

/**
 * @param {FocusEvent & { currentTarget: HTMLInputElement }} e
 */
function validateTc(e) {
    if (!canEdit || !event) return;
    if (e.currentTarget.value === "--:--:--:--") return;

    tc = tcLib.tcValidate(
        e.currentTarget.value,
        projectState.frameRate,
        projectState.dropFrame,
    );
    if (!tc) {
        formatFromEvent();
        return;
    }

    const egIdx = projectState.selected;
    const eventId = event.id;
    const nextTc =
        tcLib.tcToSec(tc, projectState.frameRate, projectState.dropFrame) -
        projectState.incode;

    commitHistory({
        name: "update timecode",
        eventGroup: egIdx,
        mutate: {
            eventGroupState: (draft) => {
                const idx = draft[egIdx].events.findIndex(
                    (item) => item.id === eventId,
                );
                if (idx > -1) {
                    draft[egIdx].events[idx][tcType] = nextTc;
                }
            },
        },
    });
}

/**
 * @param {MouseEvent} e
 */
function updateCurrentTime(e) {
    e.stopPropagation();
    if (!event) return;
    const sec = event[tcType];
    if (
        lockState.video &&
        typeof player !== "undefined" &&
        player &&
        sec !== false &&
        sec != null &&
        !Number.isNaN(sec)
    ) {
        player.currentTime = sec;
    }
}

/**
 * @param {KeyboardEvent} e
 */
function frameByFrame(e) {
    if (!canEdit || !event) return;

    if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;

    e.preventDefault();
    e.stopPropagation();

    // Ensure numeric base for nudge
    if (event[tcType] === false || event[tcType] == null) {
        event[tcType] = 0;
    }

    const delta = tcLib.framesToSec(1, projectState.frameRate);
    if (e.key === "ArrowUp") {
        event[tcType] += delta;
    } else {
        event[tcType] -= delta;
    }

    tc = tcLib.secToTc(
        event[tcType] + projectState.incode,
        projectState.frameRate,
        projectState.dropFrame,
    );

    if (lockState.video && typeof player !== "undefined" && player) {
        player.currentTime = event[tcType];
        audioScrub();
    }
}

/**
 * @param {KeyboardEvent & { currentTarget: HTMLInputElement }} e
 */
function onKeyUp(e) {
    if (e.key === "Enter") {
        validateTc(/** @type {any} */ (e));
        e.currentTarget.blur();
    }
}
</script>

<!-- svelte-ignore a11y_click_events_have_key_events -->
<!-- svelte-ignore a11y_no_static_element_interactions -->
<div
    class="tc-cell"
    onclick={(e) => e.stopPropagation()}
    onmousedown={(e) => e.stopPropagation()}>
    <button
        type="button"
        class="tc-stamp"
        aria-label={m.input_tc_set_button_aria_label({ tcType })}
        title={m.input_tc_set_button_title({ tcType })}
        disabled={!canEdit}
        onclick={setTc}>
        <i class="bi bi-clock" aria-hidden="true"></i>
    </button>
    <input
        class="tc-input"
        type="text"
        aria-label={m.input_tc_input_aria_label({ tcType })}
        disabled={!canEdit}
        bind:value={tc}
        title={tc}
        onclick={updateCurrentTime}
        onkeydown={frameByFrame}
        onkeyup={onKeyUp}
        onblur={(e) => {
            focused = false;
            validateTc(e);
        }}
        onfocus={(e) => {
            focused = true;
            e.currentTarget.select();
        }}
    />
</div>

<style>
.tc-cell {
    display: inline-flex;
    align-items: center;
    gap: 0.2rem;
    width: 100%;
    height: 100%;
    min-width: 0;
}
.tc-stamp {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin: 0;
    padding: 0;
    border: none;
    background: transparent;
    box-shadow: none;
    color: inherit;
    opacity: 0.55;
    line-height: 1;
    font-size: 0.75rem;
    cursor: pointer;
}
.tc-stamp:hover:not(:disabled) {
    opacity: 1;
}
.tc-stamp:disabled {
    opacity: 0.3;
    cursor: not-allowed;
}
.tc-input {
    flex: 1 1 auto;
    min-width: 0;
    margin: 0;
    padding: 0;
    border: none;
    border-radius: 0;
    background: transparent;
    box-shadow: none;
    color: inherit;
    font: inherit;
    font-size: inherit;
    font-variant-numeric: tabular-nums;
    line-height: 1.2;
    outline: none;
}
.tc-input:focus {
    border-bottom: 1px solid currentColor;
    opacity: 1;
}
.tc-input:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}
</style>
